// Auth context — Phase Auth-Migration-1 Phase 4 (post-soak).
//
// Thin wrapper around BetterAuth's useSession. Preserves the legacy
// `useAuth()` interface ({ user, isLoading, signIn, signUp, signOut })
// so the 143 existing call sites continue to work unchanged.
//
// Pre-Phase-4 this file was 222 lines of bespoke Lucia session
// management (manual cookie reads, /api/auth/me polls, custom retry
// logic). All of that is now handled by BetterAuth's React SDK; we
// only need the adapter layer.

import { createContext, useContext, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { authClient, useSession } from './auth-client'
import { clearUserScopedStorage } from './storage-keys'
import { trpc } from '~/utils/trpc'
import {
  shouldApplyLifecycleEmailOptOut,
  stripLifecycleOptOutParam,
} from './lifecycle-email-optout'
import { TermsAcceptanceGate } from '~/components/auth/TermsAcceptanceGate'

// User shape exposed to call sites. Lives here (post-Phase-4) instead of in
// the now-deleted ./lucia stub. Kept compatible with the legacy AuthUser
// interface so the 143 useAuth() consumers don't need to change.
export interface AuthUser {
  id: string
  email: string
  name: string | null
  image?: string
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null }>
  /**
   * Revokes the server session and, only on confirmed revocation, hard-navigates
   * away. Resolves with `{ error }` (mirroring signIn/signUp) so callers that
   * care can branch; callers that don't can keep `await signOut()`.
   */
  signOut: () => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const session = useSession()
  const { t } = useTranslation()

  // useSession returns { data, isPending, error }. Map to our legacy shape.
  const user: AuthUser | null = session.data?.user
    ? {
        id: session.data.user.id,
        email: session.data.user.email,
        name: session.data.user.name ?? null,
        image: session.data.user.image ?? undefined,
      }
    : null

  const isLoading = session.isPending

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email })
    } else {
      Sentry.setUser(null)
    }
  }, [user?.id, user?.email])

  // B-new-870 (LSSI-CE Art. 21.2) — apply the lifecycle-email opt-out ticked
  // at signup, on the FIRST authenticated landing after account creation.
  //
  // This used to live in dashboard.overview.lazy.tsx, but since B-new-887
  // every marketing CTA sends new visitors to
  // `/{locale}/sign-up?redirect=/{locale}/jobs?...`, so most new accounts
  // land on `/jobs`, never `/dashboard/overview` — the opt-out was silently
  // dropped for the majority of real sign-ups. AuthProvider wraps every
  // route (main.tsx), authenticated or not, so it runs here instead.
  //
  // Cross-device-authoritative source is the `lcOptOut=1` query param
  // carried on the magic-link callback URL (sign-up.lazy.tsx's
  // buildMagicLinkCallbackUrl) — it works when the link is opened on a
  // different device than the one used to sign up. sessionStorage is a
  // same-device-only fallback.
  //
  // Sets all four Tier 2 lifecycle flags to false via the SAME upsert
  // `updateEmailPreferences` uses everywhere else, so a real
  // `email_preferences` row with the flags explicitly false is created —
  // the cron's current behaviour of treating a missing row as "send" is not
  // relied on here, since that could change. jobMatchAlert is intentionally
  // excluded: it is not one of the four "lifecycle" emails this
  // notice/checkbox covers.
  const updateEmailPreferencesMutation = trpc.user.updateEmailPreferences.useMutation()
  const lifecycleOptOutAppliedRef = useRef(false)
  useEffect(() => {
    // Gated on authentication: signed-out visitors on /jobs must never fire
    // this (there is no account yet to attach preferences to), and
    // `isLoading` means the session hasn't resolved yet either way.
    if (isLoading || !user) return
    // Idempotency guard: a double-invoke (React StrictMode, or this effect's
    // deps re-firing) must not call the mutation twice.
    if (lifecycleOptOutAppliedRef.current) return

    let sessionStorageValue: string | null = null
    try {
      sessionStorageValue = window.sessionStorage.getItem('xeito:pendingLifecycleEmailOptOut')
    } catch {
      sessionStorageValue = null
    }
    if (!shouldApplyLifecycleEmailOptOut({ search: window.location.search, sessionStorageValue })) {
      return
    }
    lifecycleOptOutAppliedRef.current = true

    // IMPORTANT: sessionStorage is cleared and the URL param is stripped
    // ONLY on a confirmed save (onSuccess below) — never before the mutation
    // has actually persisted. Clearing the pending state first (the previous
    // shape of this effect) meant a failed save (network error, session not
    // ready yet) silently lost the opt-out with no record it was ever
    // requested, and the lifecycle emails would go out anyway.
    updateEmailPreferencesMutation.mutate(
      {
        firstWeekActivation: false,
        inactivityReengagement: false,
        lowCreditBalance: false,
        profileCompleteNudge: false,
      },
      {
        onSuccess: () => {
          try {
            window.sessionStorage.removeItem('xeito:pendingLifecycleEmailOptOut')
          } catch {
            // Non-fatal — worst case the mutation fires again on a later
            // landing, guarded again by lifecycleOptOutAppliedRef for THIS mount.
          }
          // Strip the URL param, preserving every other query param (e.g.
          // /jobs?country=DE&q=react&lcOptOut=1 -> /jobs?country=DE&q=react)
          // so a refresh doesn't re-trigger and it never leaks into
          // shared/bookmarked links.
          if (new URLSearchParams(window.location.search).has('lcOptOut')) {
            const cleanUrl = stripLifecycleOptOutParam(window.location.pathname, window.location.search)
            window.history.replaceState({}, '', cleanUrl)
          }
        },
        onError: (err: unknown) => {
          // Reset the idempotency guard so the NEXT authenticated landing
          // (a fresh mount, or a later render where isLoading/user?.id
          // change again) retries. Bounded, not a tight loop: this effect's
          // deps only change once per real landing, not on every render.
          // Deliberately does NOT touch sessionStorage or the URL param —
          // the opt-out must stay "pending" until a save actually succeeds,
          // or the emails get sent with no record it was ever requested.
          lifecycleOptOutAppliedRef.current = false
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
            tags: { area: 'auth', operation: 'lifecycleEmailOptOut' },
            // No PII — this mirrors the existing client-logging convention
            // in this file (see signOut's captureException above).
          })
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user?.id])

  const signIn = async (email: string, password: string) => {
    try {
      const result = await authClient.signIn.email({ email, password })
      if (result.error) {
        return { error: new Error(result.error.message ?? 'Sign-in failed') }
      }
      return { error: null }
    } catch (err: any) {
      return { error: err instanceof Error ? err : new Error('Sign-in failed') }
    }
  }

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const result = await authClient.signUp.email({
        email,
        password,
        name: name ?? '',
      })
      if (result.error) {
        return { error: new Error(result.error.message ?? 'Sign-up failed') }
      }
      return { error: null }
    } catch (err: any) {
      return { error: err instanceof Error ? err : new Error('Sign-up failed') }
    }
  }

  const signOut = async (): Promise<{ error: Error | null }> => {
    // B-new-84. The BetterAuth session cookie is HttpOnly — only a server
    // `Set-Cookie` can clear it, JS cannot. So if the revoke call fails we
    // must NOT perform the sign-out transition: doing so gives the full
    // "you're signed out" experience while the cookie stays valid, and on a
    // shared machine the next person lands straight in this account.
    //
    // BetterAuth's client returns `{ data, error }` rather than throwing
    // (same convention as signIn/signUp above), so both shapes are handled.
    let error: Error | null = null
    try {
      const result = (await authClient.signOut()) as
        | { error?: { message?: string } | null }
        | null
        | undefined
      if (result?.error) {
        error = new Error(result.error.message ?? 'Sign-out failed')
      }
    } catch (err) {
      error = err instanceof Error ? err : new Error('Sign-out failed')
    }

    if (error) {
      Sentry.captureException(error, { tags: { area: 'auth', operation: 'signOut' } })
      // Surface it and leave the user where they are — still signed in, on a
      // working authenticated page, free to retry. Silently pretending to
      // succeed is the failure mode this guards against.
      toast.error(t('errors.signOutFailed'))
      return { error }
    }

    // Revocation confirmed. Drop user-authored local state (chatbot
    // transcripts) before leaving — the destination is a different origin, so
    // navigating first would strand it on app.xeito.ai forever.
    clearUserScopedStorage()

    // Hard navigation to the marketing landing to guarantee a clean state:
    // drops every tRPC/react-query cache, clears the session hook, and avoids
    // dumping the user on a sign-in form (they just signed out — sending them
    // to the public homepage is the expected UX).
    const locale = window.location.pathname.split('/')[1] || 'en'
    const marketingUrl = import.meta.env.VITE_MARKETING_URL || 'https://xeito.ai'
    window.location.assign(`${marketingUrl}/${locale}`)
    return { error: null }
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, signIn, signUp, signOut }}>
      {/* B-new-900 — post-auth terms/privacy acceptance gate. Lives here
          (rather than a per-route wrapper) for the same reason the B-new-870
          lifecycle-email effect does: AuthProvider is the one place every
          authenticated route actually renders through. */}
      <TermsAcceptanceGate>{children}</TermsAcceptanceGate>
    </AuthContext.Provider>
  )
}

// Default no-op shape returned when useAuth is called outside the provider.
// Test/SSR contexts rely on this — matches the pre-Phase-4 behaviour.
const DEFAULT_AUTH: AuthContextType = {
  user: null,
  isLoading: false,
  signIn: async () => ({ error: new Error('Auth provider not mounted') }),
  signUp: async () => ({ error: new Error('Auth provider not mounted') }),
  signOut: async () => ({ error: new Error('Auth provider not mounted') }),
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext) ?? DEFAULT_AUTH
}
